import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  VStack,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Code
} from '@chakra-ui/react';
import HeaderMenus from '../HeaderMenus';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';

const CptHistoryViewer = () => {
  const [snapshotData, setSnapshotData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { accessToken } = useContext(AppContext);
  const location = useLocation();

  useEffect(() => {
    const fetchSnapshot = async () => {
      if (!accessToken) return;
      
      const searchParams = new URLSearchParams(location.search);
      const caccid = searchParams.get('caccid');
      
      if (!caccid) {
        setError("Missing 'caccid' parameter in URL.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await callApi(`/api/v1/cpt-history/snapshot?caccid=${caccid}`, accessToken);
        const data = await response.json();
        
        if (data.error) {
          setError(data.error);
        } else {
          setSnapshotData(data);
        }
      } catch (err) {
        setError("Failed to load historical snapshot.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSnapshot();
  }, [accessToken, location.search]);

  if (isLoading) {
    return (
      <Box bg="gray.50" minH="100vh">
        <HeaderMenus />
        <Flex justify="center" align="center" h="50vh">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="gray.50" minH="100vh">
        <HeaderMenus />
        <Box p={5}>
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error Loading Snapshot</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </Box>
      </Box>
    );
  }

  if (!snapshotData) return null;

  const formattedDate = new Date(snapshotData.snapshot_timestamp).toLocaleString();

  // Extract rule IDs from the adjustment log to highlight them in the rules table
  const appliedRuleIds = new Set();
  if (snapshotData.adjustment_log) {
    const regex = /\(ID:\s*(\d+)\)/;
    snapshotData.adjustment_log.forEach(log => {
      const text = typeof log === 'string' ? log : (log.description || log.message || JSON.stringify(log));
      const match = text.match(regex);
      if (match && match[1]) {
        appliedRuleIds.add(parseInt(match[1], 10));
      }
    });
  }

  // Sort rules so that applied rules appear at the top
  const sortedRules = [...snapshotData.deterministic_rules].sort((a, b) => {
    const aApplied = appliedRuleIds.has(a.id);
    const bApplied = appliedRuleIds.has(b.id);
    if (aApplied && !bApplied) return -1;
    if (!aApplied && bApplied) return 1;
    return 0; // Keep original order for ties
  });

  return (
    <Box bg="gray.50" minH="100vh">
      <HeaderMenus />
      <Box p={5}>
        <VStack spacing={6} align="stretch">
          <Alert status="info" variant="left-accent" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Historical Rule Snapshot (Read-Only)</AlertTitle>
              <AlertDescription>
                Viewing rules exactly as they existed on <strong>{formattedDate}</strong> for Case Adjustment ID: {snapshotData.caccid}.
              </AlertDescription>
            </Box>
          </Alert>

          <Box bg="white" p={5} borderRadius="md" boxShadow="sm">
            <Heading size="md" mb={4}>Applied Rules (Adjustment Log)</Heading>
            {!snapshotData.adjustment_log || snapshotData.adjustment_log.length === 0 ? (
              <Text color="gray.500">No rules were applied to this case.</Text>
            ) : (
              <Table variant="simple" size="sm" mb={8}>
                <Thead>
                  <Tr>
                    <Th>Action</Th>
                    <Th>Description</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {snapshotData.adjustment_log.map((log, index) => (
                    <Tr key={index}>
                      <Td><Badge colorScheme="green">{log.action || 'Applied'}</Badge></Td>
                      <Td>{log.description || log.message || JSON.stringify(log)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}

            <Heading size="md" mb={4} mt={8}>All Active Deterministic Rules</Heading>
            {snapshotData.deterministic_rules.length === 0 ? (
              <Text color="gray.500">No deterministic rules were active at this time.</Text>
            ) : (
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>ID</Th>
                    <Th>Type</Th>
                    <Th>Target</Th>
                    <Th>Action</Th>
                    <Th>Effective Start</Th>
                    <Th>Effective End</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedRules.map(rule => {
                    const isApplied = appliedRuleIds.has(rule.id);
                    return (
                      <Tr key={rule.id} bg={isApplied ? "green.50" : "transparent"}>
                        <Td fontWeight={isApplied ? "bold" : "normal"}>{rule.id}</Td>
                        <Td><Badge colorScheme="blue">{rule.rule_type}</Badge></Td>
                        <Td>{rule.target_name || '-'}</Td>
                        <Td><Code bg={isApplied ? "green.100" : "gray.100"}>{rule.action}</Code></Td>
                        <Td>{new Date(rule.effective_start_date).toLocaleString()}</Td>
                        <Td>{rule.effective_end_date ? new Date(rule.effective_end_date).toLocaleString() : 'Active'}</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            )}
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default CptHistoryViewer;
